import { useState } from "react";
import { readDraftFromHash, stashPendingDraft, hasPendingDraft } from "@/components/pending-free-builder-import";
import { Link } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, ArrowLeft, CheckCircle, Mail, KeyRound } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { PageMeta } from "@/components/PageMeta";
import { usePageTitle } from "@/hooks/use-page-title";
import { trackEvent } from "@/lib/analytics";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

function CheckInboxScreen({ email }: { email: string }) {
  const { resendVerification } = useAuth();
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [resendError, setResendError] = useState("");

  const handleResend = async () => {
    setResending(true);
    setResendError("");
    try {
      await resendVerification(email);
      setResent(true);
    } catch {
      setResendError("Failed to resend. Please try again.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <Card className="w-full max-w-md text-center">
        <CardHeader>
          <div className="mx-auto mb-4 flex items-center justify-center w-16 h-16 rounded-full bg-blue-50">
            <Mail className="h-8 w-8 text-blue-600" />
          </div>
          <CardTitle className="text-2xl">Check your inbox</CardTitle>
          <CardDescription>
            We sent a verification link to <strong>{email}</strong>. Click the link in the email to activate your account.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-500">Didn't receive the email? Check your spam folder or resend it.</p>
          {resent ? (
            <div className="flex items-center justify-center gap-2 rounded-md bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-800">
              <CheckCircle className="w-4 h-4 shrink-0 text-green-600" />
              <span>Verification email resent!</span>
            </div>
          ) : (
            <Button variant="outline" className="w-full" onClick={handleResend} disabled={resending}>
              {resending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Resend verification email
            </Button>
          )}
          {resendError && <p className="text-sm text-red-600">{resendError}</p>}
          <div className="text-center">
            <Link href="/login" className="text-sm text-blue-600 hover:underline font-medium">
              Back to sign in
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

const COMMON_PASSWORDS_FRONTEND = new Set([
  "password", "password1", "password123", "password1234", "password12345",
  "12345678", "123456789", "1234567890", "11111111", "00000000",
  "qwerty123", "qwertyui", "letmein1", "welcome1", "iloveyou",
  "sunshine1", "princess1", "football", "baseball1", "monkey123",
  "superman1", "batman123", "dragon123", "master123", "admin123",
  "abc12345", "abc123456", "trustno1", "shadow123", "hello123",
]);

function getPasswordStrength(pwd: string): { level: "weak" | "fair" | "strong"; label: string; color: string } {
  if (pwd.length === 0) return { level: "weak", label: "", color: "" };
  if (pwd.length < 8 || COMMON_PASSWORDS_FRONTEND.has(pwd.toLowerCase())) {
    return { level: "weak", label: "Weak", color: "#ef4444" };
  }
  const hasLower = /[a-z]/.test(pwd);
  const hasUpper = /[A-Z]/.test(pwd);
  const hasDigit = /\d/.test(pwd);
  const hasSymbol = /[^a-zA-Z0-9]/.test(pwd);
  const variety = [hasLower, hasUpper, hasDigit, hasSymbol].filter(Boolean).length;
  if (pwd.length >= 12 || (pwd.length >= 8 && variety >= 3)) {
    return { level: "strong", label: "Strong", color: "#10b981" };
  }
  return { level: "fair", label: "Fair", color: "#f59e0b" };
}

function getGoogleOAuthUrl(base: string, params?: Record<string, string>): string {
  const rdtCid = localStorage.getItem("rdt_cid");
  const refCode = sessionStorage.getItem("ref_code");
  const url = new URL(`${base}/api/auth/google`, window.location.origin);
  if (params) {
    Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
  }
  if (rdtCid) url.searchParams.set("rdt_cid", rdtCid);
  if (refCode) url.searchParams.set("ref", refCode);
  return url.pathname + url.search;
}

export default function AuthPage({ initialMode }: { initialMode?: "login" | "signup" } = {}) {
  const { login, register, resendVerification } = useAuth();
  const { toast } = useToast();
  const [isLogin, setIsLogin] = useState(initialMode !== "signup");
  usePageTitle(isLogin ? "Log In" : "Sign Up Free");
  const searchParams = new URLSearchParams(window.location.search);
  const accountDeleted = searchParams.get("deleted") === "1";
  const passwordReset = searchParams.get("passwordReset") === "1";
  const planParam = searchParams.get("plan");
  const billingParam = searchParams.get("billing");
  const refParam = searchParams.get("ref");

  if (refParam && !isLogin) {
    sessionStorage.setItem("ref_code", refParam);
  }
  // The free generator on quotrak.com links here with the draft in the URL
  // fragment. Stash it so it survives email verification; the authenticated
  // shell imports it on the first signed-in load.
  const [pendingDraft] = useState(() => {
    const fromHash = readDraftFromHash(window.location.hash);
    if (fromHash) {
      stashPendingDraft(fromHash);
      try { history.replaceState(null, "", window.location.pathname + window.location.search); } catch { /* ignore */ }
      return fromHash.mode === "invoice" ? "invoice" : "quote";
    }
    return hasPendingDraft() ? "document" : null;
  });
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [keepLoggedIn, setKeepLoggedIn] = useState(
    () => localStorage.getItem("quotrak:keep-logged-in") === "1",
  );
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [checkInboxEmail, setCheckInboxEmail] = useState<string | null>(null);
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [verificationResent, setVerificationResent] = useState(false);

  if (checkInboxEmail) {
    return <CheckInboxScreen email={checkInboxEmail} />;
  }

  const handleResendVerification = async () => {
    if (!unverifiedEmail) return;
    setResendingVerification(true);
    try {
      await resendVerification(unverifiedEmail);
      setVerificationResent(true);
    } catch {
      setError("Failed to resend verification email. Please try again.");
    } finally {
      setResendingVerification(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setUnverifiedEmail(null);
    setVerificationResent(false);
    setSubmitting(true);
    try {
      if (isLogin) {
        await login(email, password);
      } else {
        if (!name.trim()) {
          setError("Name is required");
          setSubmitting(false);
          return;
        }
        if (!agreedToTerms) {
          setError("You must agree to the Terms of Service and Privacy Policy to create an account.");
          setSubmitting(false);
          return;
        }
        const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const result = await register(name, email, password, undefined, agreedToTerms, detectedTimezone);
        trackEvent("Signup");
        if (result.emailVerificationRequired && result.email) {
          setCheckInboxEmail(result.email);
          return;
        }
        if (planParam === "pro") {
          const interval = billingParam === "annual" ? "annual" : "monthly";
          try {
            const res = await fetch(`${BASE}/api/billing/checkout`, {
              method: "POST",
              credentials: "include",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ interval, rdt_cid: localStorage.getItem("rdt_cid") || undefined }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data?.error || `Failed (${res.status})`);
            if (data.url) {
              window.location.href = data.url;
              return;
            }
            toast({
              title: "Checkout unavailable",
              description: "Account created. Please upgrade from your dashboard.",
              variant: "destructive",
            });
          } catch (checkoutErr) {
            toast({
              title: "Checkout failed",
              description: checkoutErr instanceof Error ? checkoutErr.message : "Please try again.",
              variant: "destructive",
            });
          }
        }
      }
    } catch (err) {
      if (err instanceof Error) {
        const anyErr = err as unknown as { data?: { error?: string; email?: string } };
        if (anyErr.data?.error === "EMAIL_NOT_VERIFIED" && anyErr.data?.email) {
          setUnverifiedEmail(anyErr.data.email);
          setError("Please verify your email address before signing in.");
        } else {
          setError(err.message);
        }
      } else {
        setError("Something went wrong");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <PageMeta
        title={isLogin ? "Log In — QuoTrak" : "Sign Up Free — QuoTrak"}
        description={isLogin
          ? "Log in to QuoTrak to manage your quotes, invoices, and customers."
          : "Create your free QuoTrak account. Build quotes with real-time margin tracking, send invoices, and get paid faster — no credit card required."}
        path={isLogin ? "/login" : "/signup"}
        noindex
      />
      <div className="absolute top-4 left-4">
        <a href="https://quotrak.com" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 transition-colors">
          <ArrowLeft className="h-4 w-4" />
          Back to home
        </a>
      </div>
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex items-center justify-center">
            <img src="/logo-new.png" alt="QuoTrak" style={{ height: "4rem", width: "auto", objectFit: "contain" }} />
          </div>
          <CardTitle className="text-2xl">
            {isLogin ? "Welcome back" : "Create your account"}
          </CardTitle>
          <CardDescription>
            {isLogin
              ? "Sign in to your QuoTrak workspace"
              : "Get started with QuoTrak"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {accountDeleted && (
            <div className="mb-4 flex items-start gap-2 rounded-md bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-800">
              <CheckCircle className="w-4 h-4 mt-0.5 shrink-0 text-green-600" />
              <span>Your account has been permanently deleted. All data has been removed.</span>
            </div>
          )}
          {passwordReset && isLogin && (
            <div className="mb-4 flex items-start gap-2 rounded-md bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-800">
              <KeyRound className="w-4 h-4 mt-0.5 shrink-0 text-green-600" />
              <span>Your password has been reset. You can now sign in with your new password.</span>
            </div>
          )}
          {pendingDraft && (
            <div className="mb-4 flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900">
              <CheckCircle className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
              <span>
                The {pendingDraft} you built on quotrak.com is ready to come with you. {isLogin ? "Sign in" : "Create your account"} and it'll be waiting, costs and all.
              </span>
            </div>
          )}
          <form key={isLogin ? 'login' : 'signup'} onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Smith"
                  required={!isLogin}
                  autoComplete="name"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoComplete="email"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                {isLogin && (
                  <Link href="/forgot-password" className="text-xs text-blue-600 hover:underline font-medium">
                    Forgot password?
                  </Link>
                )}
              </div>
              <Input
                id="password"
                name="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={isLogin ? "Your password" : "Min. 8 characters"}
                required
                minLength={isLogin ? 1 : 8}
                autoComplete={isLogin ? "current-password" : "new-password"}
              />
              {!isLogin && password.length > 0 && (() => {
                const strength = getPasswordStrength(password);
                return (
                  <div className="space-y-1">
                    <div className="flex gap-1">
                      {(["weak", "fair", "strong"] as const).map((level, i) => {
                        const levels = ["weak", "fair", "strong"];
                        const currentIndex = levels.indexOf(strength.level);
                        const filled = i <= currentIndex;
                        return (
                          <div
                            key={level}
                            style={{
                              flex: 1,
                              height: "4px",
                              borderRadius: "2px",
                              background: filled ? strength.color : "hsl(var(--border))",
                              transition: "background 0.2s",
                            }}
                          />
                        );
                      })}
                    </div>
                    <p style={{ fontSize: "0.75rem", color: strength.color, fontWeight: 500 }}>
                      {strength.label}
                    </p>
                  </div>
                );
              })()}
            </div>
            {isLogin && (
              <div className="flex items-center gap-2.5 pt-1">
                <input
                  id="keep-logged-in"
                  type="checkbox"
                  checked={keepLoggedIn}
                  onChange={(e) => {
                    const v = e.target.checked;
                    setKeepLoggedIn(v);
                    if (v) {
                      localStorage.setItem("quotrak:keep-logged-in", "1");
                    } else {
                      localStorage.removeItem("quotrak:keep-logged-in");
                    }
                  }}
                  className="h-4 w-4 shrink-0 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="keep-logged-in" className="text-sm text-gray-600 cursor-pointer select-none">
                  Keep me signed in on this device
                </label>
              </div>
            )}
            {!isLogin && (
              <div className="flex items-start gap-2.5 pt-1">
                <input
                  id="agree-terms"
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="agree-terms" className="text-sm text-gray-600 leading-snug cursor-pointer select-none">
                  I have read and agree to the{" "}
                  <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium">
                    Terms of Service
                  </a>{" "}
                  and{" "}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium">
                    Privacy Policy
                  </a>
                </label>
              </div>
            )}
            {error && (
              <div className="space-y-2">
                <p className="text-sm text-red-600">{error}</p>
                {unverifiedEmail && (
                  verificationResent ? (
                    <div className="flex items-center gap-2 rounded-md bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-800">
                      <CheckCircle className="w-4 h-4 shrink-0 text-green-600" />
                      <span>Verification email resent! Check your inbox.</span>
                    </div>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={handleResendVerification}
                      disabled={resendingVerification}
                    >
                      {resendingVerification && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Resend verification email
                    </Button>
                  )
                )}
              </div>
            )}
            <Button type="submit" className="w-full" disabled={submitting || (!isLogin && !agreedToTerms)}>
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isLogin ? "Sign In" : "Create Account"}
            </Button>
          </form>

          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-xs text-gray-400">
              <span className="bg-white px-2">or</span>
            </div>
          </div>

          <a
            href={getGoogleOAuthUrl(BASE)}
            onClick={(e) => {
              if (!isLogin && !agreedToTerms) {
                e.preventDefault();
                setError("You must agree to the Terms of Service and Privacy Policy to create an account.");
              }
            }}
            className="flex w-full items-center justify-center gap-3 rounded-md border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
              <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z" fill="#34A853"/>
              <path d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.961H.957C.347 6.175 0 7.55 0 9s.348 2.825.957 4.039l3.007-2.332z" fill="#FBBC05"/>
              <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z" fill="#EA4335"/>
            </svg>
            {isLogin ? "Sign in with Google" : "Sign up with Google"}
          </a>

          <div className="mt-4 text-center text-sm text-gray-600">
            {isLogin ? (
              <>
                Don't have an account?{" "}
                <button
                  type="button"
                  onClick={() => { setIsLogin(false); setError(""); setUnverifiedEmail(null); setVerificationResent(false); setAgreedToTerms(false); }}
                  className="text-blue-600 hover:underline font-medium"
                >
                  Sign up
                </button>
              </>
            ) : (
              <>
                Already have an account?{" "}
                <button
                  type="button"
                  onClick={() => { setIsLogin(true); setError(""); setUnverifiedEmail(null); setVerificationResent(false); setAgreedToTerms(false); }}
                  className="text-blue-600 hover:underline font-medium"
                >
                  Sign in
                </button>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
